import { SearchClient } from 'algoliasearch/lite';
import { UiState } from 'instantsearch.js';
import { useRouter } from 'next/router';
import React, { useCallback, useRef } from 'react';
import {
    Configure,
    InstantSearch,
    InstantSearchProps,
    InstantSearchSSRProvider,
} from 'react-instantsearch';
import { usePage } from '~/templates/pages';
import { useInstantSearchRouting } from '../hooks';
import { proxySearchClient } from '../proxy-client';
import { ALGOLIA_INDEX_MAIN } from '../variables';

type Props = Omit<
    InstantSearchProps,
    'indexName' | 'routing' | 'searchClient' | 'onStateChange' | 'future'
> & {
    searchClient?: SearchClient;
};

export const AlgoliaProductList: React.FC<Props> = ({
    children,
    searchClient = proxySearchClient,
    ...rest
}) => {
    const { data } = usePage();
    const { isReady } = useRouter();
    const routing = useInstantSearchRouting();

    const serverState = useRef(data?.plpResult?.serverState);

    /**
     * TODO(IMC-811): Remove once we have resolved hydration errors.
     */
    const onStateChange = useCallback(
        (params: { uiState: UiState; setUiState(state: UiState): void }) => {
            const { uiState, setUiState } = params;

            /**
             * There is potentially a race condition where InstantSearch will use a snapshot of
             * our router that doesn't have complete info (e.g. search parameters). This is especially
             * true when having hydration issues.
             *
             * To prevent this, we will ignore requests to change states until our router is ready.
             */
            if (isReady) setUiState(uiState);
        },
        [isReady],
    );

    return (
        <InstantSearchSSRProvider {...serverState.current}>
            <InstantSearch
                indexName={ALGOLIA_INDEX_MAIN}
                searchClient={searchClient}
                routing={routing}
                onStateChange={onStateChange}
                future={{
                    preserveSharedStateOnUnmount: true,
                }}
                {...rest}
            >
                <Configure clickAnalytics />
                {children}
            </InstantSearch>
        </InstantSearchSSRProvider>
    );
};
